import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Input, Spin, Tabs, Typography, Tooltip, Space, message } from 'antd';
import {
  ArrowLeftOutlined,
  ApartmentOutlined,
  GlobalOutlined,
  ShopOutlined,
  RobotOutlined,
  CodeOutlined,
  ReadOutlined,
  ThunderboltOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PartitionOutlined,
  OrderedListOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { PageHeader } from '@/components/PageHeader';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { getExecutorMeta, executorPalette, type ExecutorTone } from './executors';
import { workflowSourceView, stepTone } from './workflowSource';
import { WorkflowDiagram } from './WorkflowDiagram';
import { WorkflowRuns } from './WorkflowRuns';
import type { AIProcessLibrary, ProcessWorkflow, WorkflowNode, WorkflowNodeAgent, WorkflowCatalogEntry, WorkflowCatalogStep } from '@/api/types';

const { Text } = Typography;

// Detail view for one workflow. Renders the executor list as a vertical
// pipeline so the flow reads top-to-bottom like a recipe — node card +
// connector line + next node. Branching/conditional flows highlight where
// the path forks (multiple NodesConnected, ConditionalEdges populated).

export const WorkflowDetailPage = observer(function WorkflowDetailPage() {
  // /workflows/13 opens a database row (and what runs for it); /workflows/PROMPT_REVIEW opens a workflow
  // defined only in code, which has no row.
  const { id } = useParams<{ id: string }>();
  const numericId = id && /^\d+$/.test(id) ? Number(id) : NaN;
  const nameKey = id && Number.isNaN(numericId) ? decodeURIComponent(id) : null;
  const { processLibraryStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // Edit mode is page-local. Editing keeps a draft AIProcessLibrary copy
  // separate from store.selected so cancel reverts cheaply and the saved
  // baseline is still readable. Save calls the store, which persists +
  // splices into the list slice.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AIProcessLibrary | null>(null);
  const [rawJsonOpen, setRawJsonOpen] = useState(false);
  const [rawJsonText, setRawJsonText] = useState('');
  const [rawJsonError, setRawJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isNaN(numericId)) void store.loadDetail(numericId);
    else if (nameKey) void store.loadEffective(nameKey);
    return () => store.clearDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId, nameKey]);

  // When the loaded selected changes (initial load or save round-trip),
  // re-seed the draft if we're not actively editing. Avoids stomping on
  // in-progress edits but keeps draft current with reality otherwise.
  useEffect(() => {
    if (!editing && store.selected) setDraft(store.selected);
  }, [store.selected, editing]);

  function startEdit() {
    if (!store.selected) return;
    setDraft(store.selected);
    setEditing(true);
  }

  function cancelEdit() {
    setDraft(store.selected);
    setEditing(false);
  }

  async function saveEdit() {
    if (!draft) return;
    try {
      await store.saveSelected(draft);
      setEditing(false);
      message.success('Workflow saved');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Save failed');
    }
  }

  function openRawJson() {
    if (!draft && !store.selected) return;
    setRawJsonText(JSON.stringify(draft ?? store.selected, null, 2));
    setRawJsonError(null);
    setRawJsonOpen(true);
  }

  async function applyRawJson() {
    try {
      const parsed = JSON.parse(rawJsonText) as AIProcessLibrary;
      if (!parsed.id || parsed.id !== (store.selected?.id ?? -1)) {
        setRawJsonError('Id field must match the loaded workflow');
        return;
      }
      setDraft(parsed);
      setEditing(true);
      setRawJsonOpen(false);
      message.info('Applied — review and click Save to persist');
    } catch (e) {
      setRawJsonError(e instanceof Error ? e.message : String(e));
    }
  }

  if (Number.isNaN(numericId) && !nameKey) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ApartmentOutlined />}
          title="Invalid workflow id"
          description="Pick a workflow from the list to see its pipeline."
          action={
            <Link to="/workflows">
              <Button type="primary">All workflows</Button>
            </Link>
          }
        />
      </SectionCard>
    );
  }

  // When editing, render from the draft (locally mutable). When not
  // editing, render from store.selected (the saved truth).
  const eff: WorkflowCatalogEntry | null = store.effective;
  const hasRow = !!store.selected;
  // A code-only workflow has no row: render a read-only shell named after what runs.
  const shell: AIProcessLibrary | null = eff
    ? { id: 0, name: eff.name, title: eff.title, merchantId: null, workflow: { nodes: [], agents: [] } }
    : null;
  const w = editing ? draft : store.selected ?? shell;
  const nodes = w?.workflow?.nodes ?? [];
  const agents = w?.workflow?.agents ?? [];
  const isGlobal = w?.merchantId == null;
  const source = workflowSourceView(eff?.source);
  // Steps defined in code are edited by changing code; only a row that carries its own steps is editable here.
  const canEdit = hasRow && !!eff && !eff.definedInCode;
  const showEffective = !editing && !!eff;

  function patchDraft(patch: Partial<AIProcessLibrary>) {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  function patchNodeArgument(idx: number, key: string, value: unknown) {
    setDraft((prev) => {
      if (!prev?.workflow?.nodes) return prev;
      const next = { ...prev };
      const wf: ProcessWorkflow = { ...prev.workflow };
      wf.nodes = prev.workflow.nodes.map((n, i) => {
        if (i !== idx) return n;
        const args = { ...(n.arguments ?? {}) } as Record<string, unknown>;
        args[key] = value;
        return { ...n, arguments: args };
      });
      next.workflow = wf;
      return next;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 32 }}>
      <Space size={4}>
        <Link to="/workflows">
          <Button type="text" size="small" icon={<ArrowLeftOutlined />}>
            All workflows
          </Button>
        </Link>
      </Space>

      {store.detailError && (
        <Alert
          type="error"
          message="Failed to load workflow"
          description={store.detailError}
          showIcon
          closable
        />
      )}

      {store.effectiveError && !w && (
        <Alert type="error" message="Failed to load workflow" description={store.effectiveError} showIcon />
      )}

      {(store.isLoadingDetail || store.isLoadingEffective) && !w && (
        <SectionCard>
          <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        </SectionCard>
      )}

      {w && (
        <>
          <PageHeader
            icon={<ApartmentOutlined />}
            accent="violet"
            title={
              editing ? (
                <Input
                  value={w.title ?? ''}
                  onChange={(e) => patchDraft({ title: e.target.value })}
                  placeholder="Workflow title"
                  style={{ fontSize: 22, fontWeight: 700, maxWidth: 540 }}
                  size="large"
                />
              ) : (
                (showEffective ? eff?.title : null) || w.title || w.name || `Workflow #${w.id}`
              )
            }
            subtitle={
              editing ? (
                <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                  <Input
                    value={w.name ?? ''}
                    onChange={(e) => patchDraft({ name: e.target.value })}
                    placeholder="Stable identifier (e.g. CHECK_DIAGNOSTIC_REPORT)"
                    size="small"
                    style={{ maxWidth: 360, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
                  />
                  <Text type="warning" style={{ fontSize: 11 }}>
                    changing Name is a runtime breaker — most callers look workflows up by it
                  </Text>
                </span>
              ) : (
                <span>
                  <code style={{ fontSize: 12 }}>{w.name}</code>
                  {w.model?.title && <> · model {w.model.title}</>}
                  {showEffective && eff?.description && <> · {eff.description}</>}
                </span>
              )
            }
            actions={
              <Space>
                {eff && (
                  <Tooltip title={source.explain}>
                    <span>
                      <StatusPill tone={source.tone}>{source.label}</StatusPill>
                    </span>
                  </Tooltip>
                )}
                {hasRow &&
                  (isGlobal ? (
                    <StatusPill tone="violet" icon={<GlobalOutlined />}>Global row</StatusPill>
                  ) : (
                    <StatusPill tone="ink" icon={<ShopOutlined />}>Merchant #{w.merchantId}</StatusPill>
                  ))}
                {!editing && hasRow && (
                  <>
                    <Button size="small" icon={<CodeOutlined />} onClick={openRawJson}>
                      Raw JSON
                    </Button>
                    <Tooltip title={canEdit ? undefined : 'These steps are defined in code. A change ships with a deploy; the database row here holds only settings.'}>
                      <Button type="primary" size="small" icon={<EditOutlined />} onClick={startEdit} disabled={!canEdit}>
                        Edit
                      </Button>
                    </Tooltip>
                  </>
                )}
                {editing && (
                  <>
                    <Button size="small" icon={<CodeOutlined />} onClick={openRawJson}>
                      Raw JSON
                    </Button>
                    <Button size="small" icon={<CloseOutlined />} onClick={cancelEdit} disabled={store.isSaving}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      loading={store.isSaving}
                      onClick={saveEdit}
                    >
                      Save
                    </Button>
                  </>
                )}
              </Space>
            }
            helpTopic="workflows.overview"
          />

          {store.saveError && (
            <Alert
              type="error"
              message="Save failed"
              description={store.saveError}
              showIcon
              closable
            />
          )}

          {showEffective && eff && eff.notes.length > 0 && (
            <Alert
              type="info"
              showIcon
              message="How this workflow runs"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {eff.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              }
            />
          )}

          {/* Summary strip — what RUNS (effective) unless a row is being edited */}
          {showEffective && eff ? <EffectiveSummaryStrip entry={eff} isDark={isDark} /> : <SummaryStrip workflow={w} isDark={isDark} />}

          {/* Workflow body — tabs for the recipe-style pipeline view and the
              graph diagram view. Pipeline is the default since it doubles as
              the editor in edit mode; the Diagram tab is read-only and powered
              by the new WorkflowDiagramService (Option C resolution +
              [WorkflowExecutorDoc] / [SendsMessage] reflection on the backend). */}
          <SectionCard noPadding>
            <Tabs
              defaultActiveKey="pipeline"
              tabBarStyle={{ padding: '8px 18px 0 18px', marginBottom: 0 }}
              items={[
                {
                  key: 'pipeline',
                  label: (
                    <span>
                      <OrderedListOutlined /> Pipeline
                    </span>
                  ),
                  children: (
                    <div style={{ padding: '4px 18px 16px 18px' }}>
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
                      >
                        {editing
                          ? "Edit each step's operator-facing log message. Use Raw JSON for deeper changes (arguments, retry policy, edges)."
                          : 'Top-to-bottom execution. Hover any node to see its arguments and edge targets.'}
                      </Text>
                      {showEffective && eff ? (
                        eff.steps.length === 0 ? (
                          <Text type="secondary">This workflow has no steps defined.</Text>
                        ) : (
                          <EffectivePipeline steps={eff.steps} isDark={isDark} />
                        )
                      ) : nodes.length === 0 ? (
                        <Text type="secondary">This workflow has no nodes defined.</Text>
                      ) : (
                        <Pipeline
                          nodes={nodes}
                          agents={agents}
                          isDark={isDark}
                          editing={editing}
                          onLogChange={(idx, log) => patchNodeArgument(idx, 'Log', log)}
                        />
                      )}
                    </div>
                  ),
                },
                {
                  key: 'diagram',
                  label: (
                    <span>
                      <PartitionOutlined /> Diagram
                    </span>
                  ),
                  children: (
                    <div style={{ padding: '4px 18px 16px 18px' }}>
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, display: 'block', marginBottom: 12 }}
                      >
                        Each step as it runs: whether it is AI or plain code, the agent and what it loads, the
                        rules it checks, and what passes along each arrow. The panel on the right is the context
                        the workflow carries between steps, around the AI.
                      </Text>
                      {w.name ? (
                        <WorkflowDiagram workflowName={w.name} isDark={isDark} height={560} />
                      ) : (
                        <Text type="secondary">
                          Workflow has no name; cannot resolve diagram.
                        </Text>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'runs',
                  label: (
                    <span>
                      <HistoryOutlined /> Runs
                    </span>
                  ),
                  children: (
                    <div style={{ padding: '12px 18px 16px 18px' }}>
                      {w.name ? (
                        <WorkflowRuns workflowName={w.name} isDark={isDark} />
                      ) : (
                        <Text type="secondary">Workflow has no name; its runs cannot be found.</Text>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </SectionCard>

          {/* The agents this workflow runs, as resolved (name + model) */}
          {showEffective && eff && eff.agents.length > 0 && (
            <SectionCard title="Agents in this workflow" subtitle="The ai.AiAgent each agent step runs, and the model it runs on.">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {eff.agents.map((a) => (
                  <div key={a.refId} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <StatusPill tone="violet" icon={<RobotOutlined />}>{a.name || a.refId}</StatusPill>
                    {a.modelName && <Text type="secondary" style={{ fontSize: 12 }}>model {a.modelName}</Text>}
                    {a.agentId > 0 && <Text type="secondary" style={{ fontSize: 12 }}>agent #{a.agentId}</Text>}
                    {a.canonicalName && <code style={{ fontSize: 11 }}>{a.canonicalName}</code>}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Agents inline list (row editing) */}
          {!showEffective && agents.length > 0 && (
            <SectionCard
              title="Agents in this workflow"
              subtitle="Inline agent definitions referenced by AGENT_NODE_EXECUTOR steps."
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {agents.map((a) => (
                  <AgentRow key={a.id} agent={a} isDark={isDark} />
                ))}
              </div>
            </SectionCard>
          )}

          {/* Raw JSON drawer — power-user editor for the full row */}
          <Drawer
            open={rawJsonOpen}
            onClose={() => setRawJsonOpen(false)}
            placement="right"
            width={720}
            title="Raw workflow JSON"
            extra={
              <Space>
                <Button onClick={() => setRawJsonOpen(false)}>Close</Button>
                <Button type="primary" onClick={applyRawJson}>
                  Apply
                </Button>
              </Space>
            }
          >
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
              The full ai.AIProcessLibrary row. Apply parses + validates JSON, drops you into edit
              mode with the proposed change, and waits for your Save click.
            </Text>
            {rawJsonError && (
              <Alert type="error" message="Could not apply" description={rawJsonError} showIcon style={{ marginBottom: 12 }} />
            )}
            <Input.TextArea
              value={rawJsonText}
              onChange={(e) => setRawJsonText(e.target.value)}
              rows={28}
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            />
          </Drawer>
        </>
      )}
    </div>
  );
});

function SummaryStrip({ workflow, isDark }: { workflow: AIProcessLibrary; isDark: boolean }) {
  const nodes = workflow.workflow?.nodes ?? [];
  const agentCount = workflow.workflow?.agents?.length ?? 0;
  const triggerCount = workflow.triggers?.length ?? 0;
  const sysPromptCount = workflow.systemPrompts?.length ?? 0;
  const branching = nodes.some((n) => (n.conditionalEdges?.length ?? 0) > 0 || (n.nodesConnected?.length ?? 0) > 1);

  return (
    <SectionCard noPadding>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 0,
        }}
      >
        <Stat label="Steps" value={nodes.length} hint={branching ? 'with branching' : 'linear pipeline'} isDark={isDark} />
        <Stat label="Agents" value={agentCount} hint={agentCount === 0 ? 'no inline agents' : 'inline definitions'} isDark={isDark} />
        <Stat label="Triggers" value={triggerCount} hint={triggerCount === 0 ? 'manual run' : 'event subscriptions'} isDark={isDark} />
        <Stat label="System prompts" value={sysPromptCount} hint={sysPromptCount === 0 ? 'none configured' : 'global instructions'} isDark={isDark} />
        <Stat
          label="Threading"
          value={workflow.useSameThread ? 'Same' : 'New'}
          valueSize="medium"
          hint={workflow.useSameThread ? 'reuses thread' : 'fresh thread per run'}
          isDark={isDark}
        />
      </div>
    </SectionCard>
  );
}

function Stat({
  label,
  value,
  hint,
  valueSize = 'large',
  isDark,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  valueSize?: 'large' | 'medium';
  isDark: boolean;
}) {
  return (
    <div
      style={{
        padding: '14px 18px',
        borderRight: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: valueSize === 'medium' ? 18 : 22,
          fontWeight: 700,
          color: isDark ? colors.slate[50] : colors.slate[900],
          lineHeight: 1.1,
          letterSpacing: '-0.01em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 4 }}>
        {hint}
      </div>
    </div>
  );
}

function Pipeline({
  nodes,
  agents,
  isDark,
  editing,
  onLogChange,
}: {
  nodes: WorkflowNode[];
  agents: WorkflowNodeAgent[];
  isDark: boolean;
  editing: boolean;
  onLogChange: (idx: number, log: string) => void;
}) {
  const agentById = new Map(agents.map((a) => [a.id, a]));

  // Edge inference: if NodesConnected is empty, treat each node as connecting
  // to the next in declaration order (which matches the runtime's chain
  // execution for simple linear flows). Otherwise honor explicit edges.

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {nodes.map((node, i) => {
        const isLast = i === nodes.length - 1;
        const explicitEdges = node.nodesConnected ?? [];
        const conditional = node.conditionalEdges ?? [];
        const fanOut = explicitEdges.length > 1 || conditional.length > 0;

        return (
          <div key={node.id ?? i}>
            <NodeCard
              index={i + 1}
              node={node}
              agent={node.useAgent ? agentById.get(node.useAgent) : undefined}
              isDark={isDark}
              editing={editing}
              onLogChange={(log) => onLogChange(i, log)}
            />
            {!isLast && (
              <Connector
                isDark={isDark}
                fanOut={fanOut}
                edges={
                  conditional.length > 0
                    ? conditional.map((e) => ({
                        target: e.targetNodeId,
                        condition: e.condition || (e.isDefault ? 'else' : 'on condition'),
                      }))
                    : explicitEdges.map((t) => ({ target: t }))
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function NodeCard({
  index,
  node,
  agent,
  isDark,
  editing,
  onLogChange,
  labelOverride,
  toneOverride,
}: {
  index: number;
  node: WorkflowNode;
  agent: WorkflowNodeAgent | undefined;
  isDark: boolean;
  editing: boolean;
  onLogChange: (log: string) => void;
  /** The step's own title ([WorkflowExecutorDoc]) when known — the frontend map only knows older executors. */
  labelOverride?: string;
  toneOverride?: ExecutorTone;
}) {
  const baseMeta = getExecutorMeta(node.classId);
  const meta = { ...baseMeta, label: labelOverride ?? baseMeta.label };
  const palette = executorPalette(toneOverride ?? baseMeta.tone, isDark);
  const log = (node.arguments?.['Log'] as string) ?? '';
  const promptName = (node.arguments?.['PromptName'] as string) ?? null;
  const knowledgeIds = node.knowledge?.items?.map((i) => i.knowledgeId).filter(Boolean) ?? [];

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'stretch',
      }}
    >
      {/* Step number gutter */}
      <div
        style={{
          flexShrink: 0,
          width: 36,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: palette.iconBg,
            color: palette.fg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            fontFamily: "'JetBrains Mono', monospace",
            border: `1px solid ${palette.border}`,
          }}
        >
          {index}
        </div>
      </div>

      {/* Node body */}
      <div
        style={{
          flex: 1,
          background: palette.bg,
          border: `1px solid ${palette.border}`,
          borderRadius: 10,
          padding: '12px 14px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: palette.fg, fontSize: 14, lineHeight: 1 }}>{meta.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: palette.fg }}>{meta.label}</span>
            <span
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace",
                color: isDark ? colors.slate[400] : colors.slate[500],
              }}
            >
              {node.classId}
            </span>
          </div>
          {node.start && <StatusPill tone="green" icon={<ThunderboltOutlined />}>start</StatusPill>}
          {node.hideResult && <StatusPill tone="slate">silent</StatusPill>}
        </div>

        {editing ? (
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: isDark ? colors.slate[400] : colors.slate[500],
                marginBottom: 4,
              }}
            >
              Operator log message (Arguments.Log)
            </div>
            <Input
              value={log}
              onChange={(e) => onLogChange(e.target.value)}
              placeholder='e.g. "Analyzing nutrition"'
              size="small"
            />
          </div>
        ) : (
          log && (
            <div style={{ marginTop: 8, fontSize: 13, color: isDark ? colors.slate[100] : colors.slate[800], lineHeight: 1.45 }}>
              “{log}”
            </div>
          )
        )}

        {/* Inline metadata pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {agent && (
            <Tooltip title={agent.instructions || agent.name}>
              <span>
                <StatusPill tone="violet" icon={<RobotOutlined />}>
                  {agent.name || agent.id}
                  {agent.plugins && agent.plugins.length > 0 ? ` · ${agent.plugins.length} plugin${agent.plugins.length === 1 ? '' : 's'}` : ''}
                </StatusPill>
              </span>
            </Tooltip>
          )}
          {node.useAgent && !agent && <StatusPill tone="slate" icon={<RobotOutlined />}>{node.useAgent}</StatusPill>}
          {promptName && <StatusPill tone="ink"><CodeOutlined /> {promptName}</StatusPill>}
          {knowledgeIds.length > 0 && (
            <StatusPill tone="green" icon={<ReadOutlined />}>
              {knowledgeIds.length} KB
            </StatusPill>
          )}
          {node.threadKey && <StatusPill tone="slate">thread: {node.threadKey}</StatusPill>}
          {node.retry && (
            <StatusPill tone="amber">
              retry ×{node.retry.maxRetries ?? 3}
            </StatusPill>
          )}
          {node.timeout && (
            <StatusPill tone="amber">
              timeout {Math.round((node.timeout.timeoutMs ?? 300000) / 1000)}s
            </StatusPill>
          )}
          {node.interaction?.required && (
            <StatusPill tone="violet">awaits user input</StatusPill>
          )}
        </div>
      </div>
    </div>
  );
}

function Connector({
  isDark,
  fanOut,
  edges,
}: {
  isDark: boolean;
  fanOut: boolean;
  edges: { target: string; condition?: string }[];
}) {
  const lineColor = isDark ? colors.slate[600] : colors.slate[300];

  if (!fanOut) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: 14 }}>
        <div
          style={{
            width: 2,
            height: 18,
            background: lineColor,
            marginLeft: 7,
          }}
        />
      </div>
    );
  }

  // Fan-out: show a labeled path per edge, all converging at the next node
  // shown below. We don't actually re-order to the targets here (they're
  // shown by id, with the labels indicating the conditions); the goal is to
  // make the *fact of branching* visible without claiming we render the
  // graph correctly.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingLeft: 14, gap: 4, padding: '6px 0 6px 14px' }}>
      <div style={{ width: 2, height: 8, background: lineColor, marginLeft: 7 }} />
      {edges.map((e, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500] }}>
          <span
            style={{
              width: 16,
              height: 2,
              background: lineColor,
              marginLeft: 0,
            }}
          />
          <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {e.condition ? `${e.condition} → ` : '→ '}
            {e.target}
          </span>
        </div>
      ))}
      <div style={{ width: 2, height: 8, background: lineColor, marginLeft: 7 }} />
    </div>
  );
}

function AgentRow({ agent, isDark }: { agent: WorkflowNodeAgent; isDark: boolean }) {
  return (
    <div
      style={{
        padding: '10px 14px',
        borderRadius: 10,
        background: isDark ? colors.violet[700] + '22' : colors.violet[50],
        border: `1px solid ${isDark ? colors.violet[700] + '66' : colors.violet[100]}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <RobotOutlined style={{ color: isDark ? colors.violet[100] : colors.violet[600] }} />
        <Text strong style={{ fontSize: 13.5, color: isDark ? colors.violet[100] : colors.violet[700] }}>
          {agent.name || agent.id}
        </Text>
        <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          {agent.id}
        </Text>
        {agent.agentId != null && (
          <StatusPill tone="ink">linked to agent #{agent.agentId}</StatusPill>
        )}
        {agent.plugins && agent.plugins.length > 0 && (
          <StatusPill tone="slate">{agent.plugins.length} plugin{agent.plugins.length === 1 ? '' : 's'}</StatusPill>
        )}
      </div>
      {agent.instructions && (
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
          {agent.instructions.length > 240 ? agent.instructions.slice(0, 240) + '…' : agent.instructions}
        </Text>
      )}
    </div>
  );
}

/** The summary strip for what RUNS (effective steps, resolved agents). */
function EffectiveSummaryStrip({ entry, isDark }: { entry: WorkflowCatalogEntry; isDark: boolean }) {
  const branching = entry.steps.some((st) => st.next.length > 1);
  const agentNames = entry.agents.map((a) => a.name).filter(Boolean);
  return (
    <SectionCard noPadding>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 0 }}>
        <Stat label="Steps" value={entry.steps.length} hint={branching ? 'with branching' : 'linear pipeline'} isDark={isDark} />
        <Stat
          label="Agents"
          value={entry.agents.length}
          hint={agentNames.length > 0 ? agentNames.join(', ') : 'no agent steps'}
          isDark={isDark}
        />
        <Stat label="Triggers" value={entry.triggerCount} hint={entry.triggerCount === 0 ? 'started by the app' : 'event subscriptions'} isDark={isDark} />
        <Stat label="System prompts" value={entry.systemPromptCount} hint={entry.systemPromptCount === 0 ? 'none configured' : 'global instructions'} isDark={isDark} />
        <Stat
          label="Threading"
          value={entry.reusesThread ? 'Same' : 'Per run'}
          valueSize="medium"
          hint={entry.reusesThread ? 'reuses thread' : 'fresh context each run'}
          isDark={isDark}
        />
      </div>
    </SectionCard>
  );
}

/** The pipeline as it runs, from the catalog's steps (titles from [WorkflowExecutorDoc], agent names resolved). */
function EffectivePipeline({ steps, isDark }: { steps: WorkflowCatalogStep[]; isDark: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {steps.map((step, i) => {
        const node: WorkflowNode = {
          id: step.id,
          classId: step.classId ?? '',
          start: step.isStart,
          useAgent: step.agentRefId ?? undefined,
          nodesConnected: step.next,
          arguments: step.log ? { Log: step.log } : {},
        } as WorkflowNode;
        const agent: WorkflowNodeAgent | undefined = step.isAgent
          ? ({
              id: step.agentRefId ?? step.id,
              name: [step.agentName ?? step.agentRefId, step.agentModel].filter(Boolean).join(' · '),
            } as WorkflowNodeAgent)
          : undefined;
        return (
          <div key={step.id ?? i}>
            <NodeCard
              index={i + 1}
              node={node}
              agent={agent}
              isDark={isDark}
              editing={false}
              onLogChange={() => {}}
              labelOverride={step.isAgent ? 'Agent turn' : step.title}
              toneOverride={stepTone(step.category, step.isAgent)}
            />
            {i < steps.length - 1 && (
              <Connector isDark={isDark} fanOut={step.next.length > 1} edges={step.next.map((t) => ({ target: t }))} />
            )}
          </div>
        );
      })}
    </div>
  );
}
